import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as imageLibraryActions from '@redux/modules/imageLibrary'

import defaultFormState, { requiredFields } from '@models/imageLibrary'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { ImageLibraryModel, ImageLibraryRequestOptions } from '@models/types'

export const generateUrls = (imageLibrary?: Partial<ImageLibraryModel>, options?: { prefixRoute?: string }) => {
  const { id } = imageLibrary || {}

  const { prefixRoute = 'systemManager' } = options || {}

  return {
    editImageLibraryDetailsUrl: `#/systemManager/imageLibraries/${id}/edit/details`,
    editImageLibraryUrl: `#/systemManager/imageLibraries/${id}/edit/`,
    imageLibrariesIndexUrl: `#/${prefixRoute}/imageLibraries`,
  }
}

type CreateImageLibraryParams = {
  dispatch: AppDispatch,
  imageLibraryParams: Partial<ImageLibraryModel>,
  requestOptions?: ImageLibraryRequestOptions,
}

const createImageLibrary = (params: CreateImageLibraryParams) => {
  const { dispatch, imageLibraryParams, requestOptions } = params
  const { createImageLibrary: createFn } = imageLibraryActions

  return dispatch(createFn(imageLibraryParams, requestOptions))
}

type UpdateImageLibraryParams = {
  dispatch: AppDispatch,
  imageLibrary: ImageLibraryModel,
  imageLibraryParams: Partial<ImageLibraryModel>,
  requestOptions?: ImageLibraryRequestOptions,
}

const updateImageLibrary = (params: UpdateImageLibraryParams) => {
  const {
    dispatch, imageLibrary, imageLibraryParams, requestOptions,
  } = params
  const { updateImageLibrary: updateFn } = imageLibraryActions

  const updatedParams = {
    id: imageLibrary.id,
    ...imageLibraryParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteImageLibraryParams = {
  dispatch: AppDispatch,
  imageLibrary: DeleteParams<ImageLibraryModel>,
}

const deleteImageLibrary = (params: DeleteImageLibraryParams) => {
  const { dispatch, imageLibrary } = params
  const { deleteImageLibrary: deleteFn } = imageLibraryActions

  return dispatch(deleteFn(imageLibrary))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useImageLibraryForm(
  imageLibrary: Partial<ImageLibraryModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const imageLibraryForm = useForm(
    defaultFormState,
    { entity: imageLibrary, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [imageLibrary.id, imageLibrary.cache_key],
  )

  return {
    ...imageLibraryForm,
  }
}

export const useRelations = (imageLibrary: Partial<ImageLibraryModel> = {}) => {
  const { cover_image_id, owner_id, owner_type } = imageLibrary

  const { agencies, brands, images } = useSelector(reduxState => reduxState.entities)

  const agency = owner_type === 'Agency' && owner_id && agencies[owner_id] ? agencies[owner_id] : {}
  const brand = owner_type === 'Brand' && owner_id && brands[owner_id] ? brands[owner_id] : {}

  const coverImage = cover_image_id && images[cover_image_id] ? images[cover_image_id] : {}

  return {
    agency,
    brand,
    coverImage,
  }
}

function useImageLibrary(initEntity: Partial<ImageLibraryModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: imageLibrary }: { entity: ImageLibraryModel } = useLatestEntity(initEntity, 'imageLibraries')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, brand, coverImage } = useRelations(imageLibrary)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.imageLibraries)

  const { isAgencyAdmin } = useCurrentUser()
  const editable = !(isAgencyAdmin && imageLibrary.owner_type === 'Brand')

  return {
    callbacks: {
      createImageLibrary: (
        imageLibraryParams: Partial<ImageLibraryModel>,
        entityOptions?: ImageLibraryRequestOptions,
      ) => (
        createImageLibrary({ imageLibraryParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditImageLibrary: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditImageLibraryModal',
        payload: { imageLibrary },
      }),
      deleteImageLibrary: () => deleteImageLibrary({ dispatch, imageLibrary }),
      updateImageLibrary: (
        imageLibraryParams: Partial<ImageLibraryModel>,
        entityOptions?: ImageLibraryRequestOptions,
      ) => (
        updateImageLibrary({
          imageLibrary, imageLibraryParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    coverImage,
    creating,
    deleting,
    editable,
    imageLibrary,
    loading: loading && !loadedIds.includes(imageLibrary.id),
    agency,
    brand,
    updating,
    urls: generateUrls(imageLibrary),
  }
}

export default useImageLibrary
